import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Build as BuildIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AccessTime as TimeIcon,
  Done as DoneIcon,
  HourglassEmpty as PendingIcon
} from '@mui/icons-material';
import { format, differenceInDays, parseISO, formatDistance } from 'date-fns';
import { pl } from 'date-fns/locale';

const WorkshopStats = ({ data, selectedMonth }) => {
  const [sortBy, setSortBy] = useState('deadline');
  const [workshopItems, setWorkshopItems] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    inProgress: 0,
    completed: 0,
    delayed: 0,
    avgCompletionTime: 0
  });
  
  // Przygotowanie danych pracowni
  useEffect(() => {
    if (!data || data.length === 0) return;
    
    // Sortowanie elementów pracowni
    const sortedItems = [...data].sort((a, b) => {
      switch (sortBy) {
        case 'deadline':
          const deadlineA = a.deadline ? new Date(a.deadline) : new Date(9999, 11, 31);
          const deadlineB = b.deadline ? new Date(b.deadline) : new Date(9999, 11, 31);
          return deadlineA - deadlineB;
        case 'priority':
          const priorityValues = { high: 3, medium: 2, low: 1 };
          return (priorityValues[b.priority] || 0) - (priorityValues[a.priority] || 0);
        case 'status':
          return a.status?.localeCompare(b.status || '');
        default:
          return 0;
      }
    });
    
    setWorkshopItems(sortedItems);
    
    // Obliczanie statystyk
    calculateStats(data);
    
  }, [data, sortBy]);
  
  // Obliczanie statystyk pracowni
  const calculateStats = (workshopData) => {
    const total = workshopData.length;
    const inProgress = workshopData.filter(item => item.status === 'in_progress').length;
    const completed = workshopData.filter(item => item.status === 'completed').length;
    
    // Obliczanie opóźnionych zadań
    const delayed = workshopData.filter(item => {
      if (item.status === 'completed') return false;
      if (!item.deadline) return false;
      
      const deadline = new Date(item.deadline);
      return deadline < new Date();
    }).length;
    
    // Obliczanie średniego czasu realizacji
    let totalCompletionTime = 0;
    let completedWithDates = 0;
    
    workshopData.forEach(item => {
      if (item.status === 'completed' && item.startDate && item.completionDate) {
        const start = new Date(item.startDate);
        const end = new Date(item.completionDate);
        const days = differenceInDays(end, start);
        
        if (days >= 0) {
          totalCompletionTime += days;
          completedWithDates++;
        }
      }
    });
    
    const avgCompletionTime = completedWithDates > 0 
      ? Math.round(totalCompletionTime / completedWithDates) 
      : 0;
    
    setStats({
      total,
      inProgress,
      completed,
      delayed,
      avgCompletionTime
    });
  };
  
  // Obsługa zmiany sortowania
  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };
  
  // Formatowanie daty
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd.MM.yyyy', { locale: pl });
    } catch (e) {
      return '-';
    }
  };
  
  // Formatowanie statusu
  const formatStatus = (status) => {
    switch (status) {
      case 'in_progress':
        return { label: 'W trakcie', color: 'warning', icon: <PendingIcon /> };
      case 'completed':
        return { label: 'Ukończone', color: 'success', icon: <DoneIcon /> };
      case 'picked_up':
        return { label: 'Odebrano', color: 'primary', icon: <CheckCircleIcon /> };
      case 'waiting':
        return { label: 'Oczekujące', color: 'info', icon: <TimeIcon /> };
      case 'delayed':
        return { label: 'Opóźnione', color: 'error', icon: <WarningIcon /> };
      default:
        return { label: 'Nieznany', color: 'default', icon: <BuildIcon /> };
    }
  };
  
  // Formatowanie priorytetu
  const formatPriority = (priority) => {
    switch (priority) {
      case 'high':
        return { label: 'Wysoki', color: 'error' };
      case 'medium':
        return { label: 'Średni', color: 'warning' };
      case 'low':
        return { label: 'Niski', color: 'info' };
      default:
        return { label: 'Nieokreślony', color: 'default' };
    }
  };
  
  // Obliczanie pozostałego czasu
  const getRemainingTime = (deadline) => {
    if (!deadline) return '-';
    
    try {
      const deadlineDate = new Date(deadline);
      const now = new Date();
      
      if (deadlineDate < now) {
        return `Przekroczono o ${formatDistance(deadlineDate, now, { locale: pl })}`;
      }
      
      return `Pozostało ${formatDistance(now, deadlineDate, { locale: pl })}`;
    } catch (e) {
      return '-';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Statystyki pracowni
      </Typography>
      
      <Grid container spacing={3}>
        {/* Karta ze statystykami */}
        <Grid item xs={12} md={4}>
          <Card elevation={3}>
            <CardHeader title="Podsumowanie" />
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Ukończone</Typography>
                  <Typography variant="body2">{stats.completed} z {stats.total}</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(stats.completed / (stats.total || 1)) * 100} 
                  color="success"
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">W trakcie</Typography>
                  <Typography variant="body2">{stats.inProgress} z {stats.total}</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(stats.inProgress / (stats.total || 1)) * 100} 
                  color="warning"
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Opóźnione</Typography>
                  <Typography variant="body2">{stats.delayed} z {stats.total}</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(stats.delayed / (stats.total || 1)) * 100} 
                  color="error"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Średni czas realizacji
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">
                    {stats.avgCompletionTime} {stats.avgCompletionTime === 1 ? 'dzień' : 
                     stats.avgCompletionTime > 1 && stats.avgCompletionTime < 5 ? 'dni' : 'dni'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Tabela z elementami pracowni */}
        <Grid item xs={12} md={8}>
          <Card elevation={3}>
            <CardHeader 
              title="Elementy w pracowni" 
              action={
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="sort-select-label">Sortuj według</InputLabel>
                  <Select
                    labelId="sort-select-label"
                    id="sort-select"
                    value={sortBy}
                    label="Sortuj według"
                    onChange={handleSortChange}
                  >
                    <MenuItem value="deadline">Termin</MenuItem>
                    <MenuItem value="priority">Priorytet</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                  </Select>
                </FormControl>
              }
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table sx={{ minWidth: 650 }} size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produkt</TableCell>
                      <TableCell>Opis</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Priorytet</TableCell>
                      <TableCell>Termin</TableCell>
                      <TableCell>Pozostały czas</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workshopItems.length > 0 ? (
                      workshopItems.map((item) => {
                        const status = formatStatus(item.status);
                        const priority = formatPriority(item.priority);
                        
                        return (
                          <TableRow key={item.id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                                  <BuildIcon />
                                </Avatar>
                                <Typography variant="body2">
                                  {item.productData?.brand || ''} {item.productData?.model || 'Produkt'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {item.description || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={status.icon}
                                label={status.label}
                                color={status.color}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={priority.label}
                                color={priority.color}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{formatDate(item.deadline)}</TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                color={
                                  item.status === 'completed' ? 'success.main' :
                                  new Date(item.deadline) < new Date() ? 'error.main' : 'text.secondary'
                                }
                              >
                                {item.status === 'completed' ? 'Ukończone' : getRemainingTime(item.deadline)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" sx={{ py: 2 }}>
                            Brak elementów w pracowni
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Karta z ostatnio ukończonymi elementami */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardHeader 
              title="Ostatnio ukończone elementy" 
              avatar={<CheckCircleIcon color="success" />}
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produkt</TableCell>
                      <TableCell>Opis prac</TableCell>
                      <TableCell>Data rozpoczęcia</TableCell>
                      <TableCell>Data ukończenia</TableCell>
                      <TableCell>Czas realizacji</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workshopItems
                      .filter(item => item.status === 'completed')
                      .slice(0, 5)
                      .map((item) => (
                        <TableRow key={`completed-${item.id}`} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {item.productData?.brand || ''} {item.productData?.model || 'Produkt'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {item.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDate(item.startDate)}</TableCell>
                          <TableCell>{formatDate(item.completionDate)}</TableCell>
                          <TableCell>
                            {item.startDate && item.completionDate ? (
                              <Typography variant="body2">
                                {formatDistance(
                                  new Date(item.startDate),
                                  new Date(item.completionDate),
                                  { locale: pl }
                                )}
                              </Typography>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    {workshopItems.filter(item => item.status === 'completed').length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" sx={{ py: 2 }}>
                            Brak ukończonych elementów
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WorkshopStats;
